import { useCallback, useEffect, useState } from "react";

/**
 * The address bar, as state.
 *
 * Small enough to own rather than take a routing dependency for: this app has
 * one routed area - the guide - and needs exactly three things from a router.
 * Read the path, change it without a reload, and notice the back button.
 *
 * The point of doing it at all is that a guide page becomes a link. It can be
 * bookmarked, sent to someone, or opened in a new tab from its own contents
 * list, none of which is true of a page that only exists while a flag is set.
 */
export function useRoute(): [string, (path: string) => void] {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    // Back and forward change the URL without going through `navigate`.
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: string) => {
    if (next === window.location.pathname) return;
    window.history.pushState(null, "", next);
    setPath(next);
    window.scrollTo({ top: 0 });
  }, []);

  return [path, navigate];
}

/**
 * Handles a click on an in-app link.
 *
 * Left-click alone is routed; anything else - a modifier, the middle button -
 * is left to the browser, so "open in new tab" keeps working. That is the
 * behaviour an `<a href>` promises, and the reason these are anchors rather
 * than buttons.
 */
export function routedClick(
  navigate: (path: string) => void,
  to: string,
): (event: React.MouseEvent<HTMLAnchorElement>) => void {
  return (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };
}
